import { useEffect } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useSpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';
import { useProjectState } from '@jira/platform__onboarding-quickstart-core/src/services/index.tsx';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { useContainerContext } from '@jira/platform__services-providers-container-context/src/main.tsx';
import type { ProjectContext as ProjectContextType } from '@jira/router-resources__project-context/src/ProjectContext.types.tsx';
import { BUSINESS } from '@jira/servicedesk__onboarding-checklist/src/business.constants.tsx';
import { useJsmOnboardingType } from '@jira/servicedesk__onboarding-checklist/src/useJsmOnboardingType.tsx';

export const useQuickstart = (
	projectContextData: ProjectContextType | null | undefined,
	containerType: 'project' | 'plan' = 'project',
): boolean => {
	const [{ data: containerContext }] = useContainerContext();
	const spaStatePageReady = useSpaStatePageReady();
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();

	const [projectState] = useProjectState();

	const projectId = projectContextData?.projectId ?? projectState?.projectId;
	const projectType = projectContextData?.projectType ?? projectState?.projectType;

	useEffect(() => {
		fetchOnboardingType(projectId, projectType);
		// oxlint-disable-next-line react/exhaustive-effect-dependencies
	}, [jsmOnboardingData, fetchOnboardingType, projectId, projectType]);

	if (
		containerType === 'project' &&
		projectType != null &&
		projectType !== SOFTWARE_PROJECT &&
		projectType !== CORE_PROJECT &&
		projectType !== SERVICE_DESK_PROJECT
	) {
		// If containerType is project and projectContext is present and it is not software, core or service management, don't render
		return false;
	}

	// Scope Type in classic -- scrum/kanban/undefined(non-scrum/kanban) & next-gen -- simple

	const isProjectScopeAvailable =
		containerContext && containerContext.scope && containerContext.scope.type;

	const isProjectScopeAndSpaPageReady = Boolean(isProjectScopeAvailable) && spaStatePageReady;

	const showJswExperience = fg('jira-fix-quickstart-not-showing')
		? containerType === 'project' && projectType === SOFTWARE_PROJECT && spaStatePageReady
		: isProjectScopeAndSpaPageReady;

	const showJwmExperience = fg('jira-fix-quickstart-not-showing')
		? containerType === 'project' && projectType === CORE_PROJECT
		: containerContext?.contextType === 'project' && projectType === CORE_PROJECT;

	const showJsmExperience = fg('jira-fix-quickstart-not-showing')
		? containerType === 'project' &&
			projectType === SERVICE_DESK_PROJECT &&
			jsmOnboardingData?.onboardingType === BUSINESS
		: containerContext?.contextType === 'project' &&
			projectType === SERVICE_DESK_PROJECT &&
			jsmOnboardingData?.onboardingType === BUSINESS;

	const showPlanExperience = containerType === 'plan';

	const canShowExperience =
		showJwmExperience || showJsmExperience || showJswExperience || showPlanExperience;
	return canShowExperience;
};
